import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import moment from 'moment';
import { Box, Grid, Typography } from '@mui/material';
import { toast } from 'react-toastify';

import { FHAntdDateTimePicker, FHInputField, FHSelect } from '../../../../base';
import COReadOnlyTextField from './COReadOnlyTextFields';
import { claimsActions } from '../../../../../store';
import { disabledFutureDate, usDollarFormatter } from '../../../../../helpers';
import {
  FORTA_BLUE,
  FORTA_GRAY4,
  FORTA_GRAY2,
  FORTA_GRAY3,
  FORTA_WHITE,
} from '../../../../../utils/colors';
import { ClaimPaymentStatus } from '../enum';
import { ClaimStatus } from '../constants';
import { buildClaimUpdatePaylod } from '../../claims/utils';

import type { RootState } from '../../../../../store';
import type { Moment } from 'moment';
import type { IClaim, SelectValueType } from '../../../../../utils/types';

const MAX_AMOUNT = 1000000; // 1M;

interface IClaimOverviewFinancialProps {
  isEditingInfo: boolean;
  allErrors: { [key: string]: boolean };
  onErrorUpdate: (err: { [key: string]: boolean }) => void;
}

interface IClaimOverviewFinancialState {
  payment_status: string;
  date_finalized: string | null;
  amount_insurance_paid: number | null;
  co_pay: number | null;
  co_insurance: number | null;
  deductible: number | null;
  adjustments: number | null;
  date_deposit_cleared_in_bank: string | null;
  amount_patient_paid: number | null;
  date_patient_paid: string | null;
  patient_responsibility: number | null;
  amount_paid_to_subscriber: number | null;
}

const claimOverviewFinancialInitialState = {
  payment_status: ClaimPaymentStatus.NOT_PAID,
  date_finalized: null,
  amount_insurance_paid: null,
  co_pay: null,
  co_insurance: null,
  deductible: null,
  adjustments: null,
  date_deposit_cleared_in_bank: null,
  amount_patient_paid: null,
  date_patient_paid: null,
  patient_responsibility: null,
  amount_paid_to_subscriber: null,
};

function ClaimOverviewFinancial({
  isEditingInfo,
  allErrors,
  onErrorUpdate,
}: IClaimOverviewFinancialProps) {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const { current: claim, loading: claimsLoading } = useSelector(
    (state: RootState) => state.claims,
  );

  const [coFinancialState, setCOFinancialState] = useState<IClaimOverviewFinancialState>(
    claimOverviewFinancialInitialState,
  );

  const disableClaimInfoUpdate = claimsLoading || claim?.status === ClaimStatus.UNBILLABLE;

  useEffect(() => {
    if (!claim) return;
    if (
      claim.payment_status === ClaimPaymentStatus.PAID_TO_SUBSCRIBER &&
      !claim.amount_paid_to_subscriber
    ) {
      onErrorUpdate({ amount_paid_to_subscriber: true });
    }
  }, [claim]);

  const updateStateFromClaimData = (claim: IClaim) => {
    setCOFinancialState({
      payment_status: claim.payment_status ?? ClaimPaymentStatus.NOT_PAID,
      date_finalized: claim.date_finalized ?? null,
      amount_insurance_paid: claim.amount_insurance_paid ?? null,
      co_pay: claim.co_pay ?? null,
      co_insurance: claim.co_insurance ?? null,
      deductible: claim.deductible ?? null,
      adjustments: claim.adjustments ?? null,
      date_deposit_cleared_in_bank: claim.date_deposit_cleared_in_bank ?? null,
      amount_patient_paid: claim.amount_patient_paid ?? null,
      date_patient_paid: claim.date_patient_paid ?? null,
      patient_responsibility: claim.patient_responsibility ?? null,
      amount_paid_to_subscriber: claim.amount_paid_to_subscriber ?? null,
    });
  };

  // Fill form data from claim response and handle null values
  useEffect(() => {
    if (!claim) return;
    updateStateFromClaimData(claim);
  }, [claim]);

  const handleDateUpdate = (dateKey: keyof IClaimOverviewFinancialState, value: Moment) => {
    if (
      dateKey === 'date_finalized' &&
      claim?.date_submitted_to_clearinghouse &&
      moment(moment(value).format('YYYY-MM-DD')).isBefore(
        moment(claim.date_submitted_to_clearinghouse).format('YYYY-MM-DD'),
      )
    ) {
      toast.error('Date finalised should be greater than date submitted');
      return;
    }
    handleSaveField({
      [dateKey]: moment(value).format('YYYY-MM-DD'),
    });
  };

  const handleAmountUpdate = (key: keyof IClaimOverviewFinancialState, value: string | null) => {
    if (!value) {
      setCOFinancialState({
        ...coFinancialState,
        [key]: null,
      });
      return;
    }
    // Allow only 2 decimal places
    if (!/^\d+(\.\d{0,2})?$/.test(value)) {
      return;
    }
    setCOFinancialState({
      ...coFinancialState,
      [key]: value ? value : null,
    });
  };

  const handleSaveField = async (updates: any, field?: keyof IClaim) => {
    if (!claim) return;
    if (field === 'amount_patient_paid' && updates[field] > MAX_AMOUNT) {
      toast.error(`Amount cannot be greater than ${MAX_AMOUNT}`);
      if (claim) {
        updateStateFromClaimData(claim);
      }
      return;
    }
    for (const key in allErrors) {
      if (allErrors[key]) {
        toast.error('Ensure all highlighted fields are completed.');
        if (claim) {
          updateStateFromClaimData(claim);
        }
        return;
      }
    }
    if (field && claim[field] === updates[field]) {
      return;
    }

    setCOFinancialState({
      ...coFinancialState,
      ...updates,
    });

    const data = buildClaimUpdatePaylod(claim, updates);
    const response = await dispatch(
      claimsActions.updateClaim({
        getAccessTokenSilently,
        claimId: claim?.id,
        data,
      }),
    );

    if (!response.payload) {
      setCOFinancialState({
        payment_status: claim.payment_status ?? ClaimPaymentStatus.NOT_PAID,
        date_finalized: claim.date_finalized ?? null,
        amount_insurance_paid: claim.amount_insurance_paid ?? null,
        co_pay: claim.co_pay ?? null,
        co_insurance: claim.co_insurance ?? null,
        deductible: claim.deductible ?? null,
        adjustments: claim.adjustments ?? null,
        date_deposit_cleared_in_bank: claim.date_deposit_cleared_in_bank ?? null,
        amount_patient_paid: claim.amount_patient_paid ?? null,
        date_patient_paid: claim.date_patient_paid ?? null,
        patient_responsibility: claim.patient_responsibility ?? null,
        amount_paid_to_subscriber: claim.amount_paid_to_subscriber ?? null,
      });
    }
  };

  const handleUpdatePaymentStatus = async (value: SelectValueType) => {
    if (!claim) return;
    const paymentStatus = Object.values(ClaimPaymentStatus).find(
      (status) => status === value,
    );
    if (!paymentStatus) return;
    await dispatch(
      claimsActions.updatePaymentStatus({
        data: {
          payment_status: paymentStatus,
        },
        getAccessTokenSilently,
        claimId: claim.id,
      }),
    );
  };

  return (
    <Box>
      <Typography textAlign="left" fontSize={21} color={FORTA_BLUE.primary} mt={5} mb={2}>
        Financial info
      </Typography>
      <Grid container spacing={2} alignItems={'center'}>
        <Grid item md={3} sm={6} xs={12}>
          <Typography fontWeight="bold" textAlign="left" mb={1}>
            Payment status
          </Typography>
          {isEditingInfo ? (
            <FHSelect
              data-testid="coPaymentStatusDropdown"
              fullWidth
              displayEmpty
              inputHeight={40}
              disabled={disableClaimInfoUpdate}
              sx={{
                boxShadow: 'none',
                borderRadius: '8px',
                '& input::placeholder': { fontSize: '14px', color: FORTA_GRAY4 },
                '&.Mui-disabled': {
                  WebkitTextFillColor: FORTA_GRAY3,
                  cursor: 'not-allowed',
                },
              }}
              options={Object.values(ClaimPaymentStatus)
                .map((status) => ({
                  value: status,
                  label: status,
                }))
                .sort((a, b) => {
                  const aLabel = a.label.toLowerCase();
                  const bLabel = b.label.toLowerCase();
                  if (aLabel < bLabel) return -1;
                  if (aLabel > bLabel) return 1;
                  return 0;
                })}
              renderValue={
                coFinancialState?.payment_status
                  ? undefined
                  : () => (
                      <Typography color={FORTA_GRAY3} fontSize={14}>
                        Not paid
                      </Typography>
                    )
              }
              value={coFinancialState?.payment_status}
              onUpdate={handleUpdatePaymentStatus}
            />
          ) : (
            <COReadOnlyTextField value={coFinancialState?.payment_status} />
          )}
        </Grid>

        <Grid item md={3} sm={6} xs={12}>
          <Typography fontWeight="bold" textAlign="left" mb={1} mr={1}>
            Amount paid to subscriber
          </Typography>
          <COReadOnlyTextField
            value={
              claim?.amount_paid_to_subscriber === null
                ? null
                : usDollarFormatter.format(claim?.amount_paid_to_subscriber ?? 0)
            }
          />
        </Grid>
        <Grid item md={3} sm={6} xs={12}>
          <Typography fontWeight="bold" textAlign="left" mb={1}>
            Date finalized
          </Typography>
          {isEditingInfo ? (
            <FHAntdDateTimePicker
              data-testid="coDateFinalized"
              inputHeight={40}
              sx={{
                width: '100%',
                boxShadow: 'none',
                borderRadius: '8px',
                '&.ant-picker-disabled': {
                  backgroundColor: FORTA_WHITE,
                  '& .ant-picker-input>input[disabled]': {
                    color: FORTA_GRAY3,
                  },
                },
              }}
              format="MM/DD/YYYY"
              allowClear={false}
              disabled={disableClaimInfoUpdate}
              disabledIconColor={FORTA_GRAY2}
              disabledDate={disabledFutureDate}
              value={
                coFinancialState?.date_finalized ? moment(coFinancialState?.date_finalized) : ''
              }
              onSelect={(value: Moment) => {
                handleDateUpdate('date_finalized', value);
              }}
              error={false}
            />
          ) : (
            <COReadOnlyTextField
              value={
                coFinancialState?.date_finalized
                  ? moment(coFinancialState?.date_finalized).format('MM/DD/YYYY')
                  : null
              }
            />
          )}
        </Grid>
        <Grid item md={3} sm={6} xs={12}>
          <Box sx={{ display: 'flex' }}>
            <Typography fontWeight="bold" textAlign="left" mb={1} mr={1}>
              Insurance amount paid
            </Typography>
          </Box>
          <COReadOnlyTextField
            value={
              claim?.amount_insurance_paid === null
                ? null
                : usDollarFormatter.format(coFinancialState?.amount_insurance_paid ?? 0)
            }
          />
        </Grid>
        <Grid item md={3} sm={6} xs={12}>
          <Typography fontWeight="bold" textAlign="left" mb={1}>
            Co-pay
          </Typography>
          <COReadOnlyTextField
            value={claim?.co_pay !== null ? usDollarFormatter.format(claim?.co_pay ?? 0) : null}
          />
        </Grid>
        <Grid item md={3} sm={6} xs={12}>
          <Typography fontWeight="bold" textAlign="left" mb={1}>
            Co-insurance
          </Typography>
          <COReadOnlyTextField
            value={
              claim?.co_insurance !== null
                ? usDollarFormatter.format(claim?.co_insurance ?? 0)
                : null
            }
          />
        </Grid>
        <Grid item md={3} sm={6} xs={12}>
          <Typography fontWeight="bold" textAlign="left" mb={1}>
            Deductible
          </Typography>
          <COReadOnlyTextField
            value={
              claim?.deductible !== null ? usDollarFormatter.format(claim?.deductible ?? 0) : null
            }
          />
        </Grid>
        <Grid item md={3} sm={6} xs={12}>
          <Typography fontWeight="bold" textAlign="left" mb={1}>
            Adjustments
          </Typography>
          <COReadOnlyTextField
            value={
              claim?.adjustments !== null ? usDollarFormatter.format(claim?.adjustments ?? 0) : null
            }
          />
        </Grid>
        <Grid item md={3} sm={6} xs={12}>
          <Typography fontWeight="bold" textAlign="left" mb={1}>
            Patient Paid Amount
          </Typography>
          {isEditingInfo ? (
            <FHInputField
              inputProps={{
                'data-testid': 'patientPaidAmount',
              }}
              fullWidth
              inputHeight={40}
              disabled={disableClaimInfoUpdate}
              placeholder="Enter amount"
              sx={{
                boxShadow: 'none',
                borderRadius: '8px',
                '& input::placeholder': { fontSize: '14px', color: FORTA_GRAY4 },
                '&.Mui-disabled input': {
                  WebkitTextFillColor: `${FORTA_GRAY3} !important`,
                  cursor: 'not-allowed',
                },
              }}
              value={coFinancialState.amount_patient_paid ?? ''}
              onChange={(e) =>
                handleAmountUpdate('amount_patient_paid', e.target.value ? e.target.value : null)
              }
              onBlur={(e) =>
                handleSaveField(
                  {
                    amount_patient_paid: e.target.value ? parseFloat(e.target.value) : null,
                  },
                  'amount_patient_paid',
                )
              }
            />
          ) : (
            <COReadOnlyTextField
              value={
                coFinancialState.amount_patient_paid === null
                  ? null
                  : usDollarFormatter.format(coFinancialState.amount_patient_paid)
              }
            />
          )}
        </Grid>
        <Grid item md={3} sm={6} xs={12}>
          <Typography fontWeight="bold" textAlign="left" mb={1}>
            Date patient payment received
          </Typography>
          {isEditingInfo ? (
            <FHAntdDateTimePicker
              data-testid="coDatePatientPaymentReceived"
              inputHeight={40}
              sx={{
                width: '100%',
                boxShadow: 'none',
                borderRadius: '8px',
                '&.ant-picker-disabled': {
                  backgroundColor: FORTA_WHITE,
                  '& .ant-picker-input>input[disabled]': {
                    color: FORTA_GRAY3,
                  },
                },
              }}
              format="MM/DD/YYYY"
              allowClear={false}
              disabled={disableClaimInfoUpdate}
              disabledIconColor={FORTA_GRAY2}
              disabledDate={disabledFutureDate}
              value={
                coFinancialState?.date_patient_paid
                  ? moment(coFinancialState?.date_patient_paid)
                  : ''
              }
              onChange={(value: Moment) => {
                handleDateUpdate('date_patient_paid', value);
              }}
              onSelect={(value: Moment) => {
                handleDateUpdate('date_patient_paid', value);
              }}
            />
          ) : (
            <COReadOnlyTextField
              value={
                coFinancialState?.date_patient_paid
                  ? moment(coFinancialState?.date_patient_paid).format('MM/DD/YYYY')
                  : null
              }
            />
          )}
        </Grid>
        <Grid item md={3} sm={6} xs={12}>
          <Typography fontWeight="bold" textAlign="left" mb={1} mr={1}>
            Patient responsibility
          </Typography>
          <COReadOnlyTextField
            value={
              coFinancialState.patient_responsibility === null
                ? null
                : usDollarFormatter.format(coFinancialState.patient_responsibility)
            }
          />
        </Grid>
        <Grid item md={3} sm={6} xs={12}>
          <Typography fontWeight="bold" textAlign="left" mb={1}>
            U&C Amount
          </Typography>
          <COReadOnlyTextField
            value={
              claim?.form_1500_data?.total_charge
                ? usDollarFormatter.format(claim?.form_1500_data?.total_charge || 0)
                : null
            }
          />
        </Grid>
      </Grid>
    </Box>
  );
}

export default ClaimOverviewFinancial;
